import { type FocusEvent, type KeyboardEvent, type Key, useCallback, useMemo, useRef, useState } from 'react';
import cn from 'clsx';
import { type WithTestId } from '@pkgs/components/types/types';
import { type AutoSuggestOptionGroup } from '@pkgs/components/hooks/useAutoSuggestOptionGroups';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { Autocomplete } from '@base-ui/react/autocomplete';

import { AutoCompletePopover } from './AutoCompletePopover';
import { AutoCompleteInput } from './AutoCompleteInput';
import { type AutoSuggestOption } from './AutoComplete';

import styles from './AutoComplete.module.css';

type AutoCompleteComboBoxProps = WithTestId<{
  label: string;
  className?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  onSelectionChange?: (key: Key | null) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  isLocationNotFound?: boolean;
  isFetching?: boolean;
  children: (item: AutoSuggestOption) => JSX.Element;
  autoSuggestOptionGroups?: AutoSuggestOptionGroup[];
}>;

export const AutoCompleteComboBox = ({
  label,
  className,
  'data-testid': dataTestId,
  children: renderItem,
  inputRef,
  inputValue = '',
  onInputChange,
  onSelectionChange,
  onBlur,
  onFocus,
  isLocationNotFound,
  isFetching,
  autoSuggestOptionGroups,
}: AutoCompleteComboBoxProps) => {
  const [isItemFocused, setIsItemFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const setSearchRadius = useSearchStore((state) => state.setSearchRadius);
  const containerRef = useRef<HTMLDivElement>(null);

  const groupedItems = useMemo(() => (autoSuggestOptionGroups ?? []).filter((group) => group.items.length > 0), [autoSuggestOptionGroups]);

  /*
    Allows selection of first item on Enter key press when no item is focused
    Note: If item is already focused then the default autocomplete behaviour is to select the focused item
  */
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !isItemFocused && autoSuggestOptionGroups) {
        // Find the first available option from either recent searches or suggestions
        const firstOption = autoSuggestOptionGroups[0]?.items[0] ?? autoSuggestOptionGroups[1]?.items[0];

        if (firstOption) {
          onSelectionChange?.(firstOption.id);
          inputRef?.current?.blur();
        }
      }
    },
    [autoSuggestOptionGroups, inputRef, isItemFocused, onSelectionChange],
  );

  const handleClearInput = useCallback(() => {
    setSearchRadius(0);
    onInputChange?.('');
    inputRef?.current?.focus();
  }, [setSearchRadius, onInputChange, inputRef]);

  const handleItemSelect = useCallback(
    (item: AutoSuggestOption) => {
      onSelectionChange?.(item.id);
    },
    [onSelectionChange],
  );

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  const handleItemHighlighted = useCallback((item: AutoSuggestOption | undefined) => {
    setIsItemFocused(item !== undefined);
  }, []);

  const handleInputFocus = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setIsOpen(true);
      onFocus?.(e);
    },
    [onFocus],
  );

  return (
    <div className={cn(styles.comboBoxContainer, styles.comboBox, className)} ref={containerRef}>
      <Autocomplete.Root
        items={groupedItems}
        value={inputValue}
        onValueChange={(value) => onInputChange?.(value)}
        open={isOpen}
        onOpenChange={handleOpenChange}
        filter={null}
        mode="none"
        itemToStringValue={(item) => item.title}
        onItemHighlighted={handleItemHighlighted}
        openOnInputClick
      >
        <AutoCompleteInput
          inputElementRef={inputRef}
          aria-label={label}
          onBlur={onBlur}
          onFocus={handleInputFocus}
          onKeyDown={onKeyDown}
          isFetching={isFetching}
          name="search"
          onClearInput={handleClearInput}
          data-testid={dataTestId ?? 'search-components_autocomplete_input'}
        />
        <AutoCompletePopover
          isLocationNotFound={isLocationNotFound}
          renderItem={renderItem}
          onItemSelect={handleItemSelect}
          portalContainer={containerRef}
        />
      </Autocomplete.Root>
    </div>
  );
};
